import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Copy, Mail, FileText, ExternalLink, Loader2, PartyPopper, ShieldCheck, FlaskConical, X } from "lucide-react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import FrameEditor from "@/components/FrameEditor";
import { FRAMES } from "@/lib/frames";
import CaptionCustomizer from "@/components/CaptionCustomizer";
import ShareCaptions from "@/components/ShareCaptions";
import ProjectForm from "@/components/ProjectForm";
import VerifyEmailStep from "@/components/VerifyEmailStep";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fireConfetti } from "@/lib/confetti";
import { shrinkImage } from "@/lib/img";
import { usePageMeta } from "@/lib/usePageMeta";

const EMPTY_FORM = {
  email: "", age: "", project_title: "", project_description: "", project_serves: "", project_when: "",
  city: "", state: "", quote: "", instagram: "",
  media_contact_name: "", media_contact_email: "", media_contact_phone: "", gallery_opt_in: true,
};

export default function Toolkit() {
  usePageMeta({ title: "#BlackGirlJoyChallenge Winner Toolkit | Southern Black Girls" });
  const canvasRef = useRef(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editMode = params.get("edit") === "1";
  const sandbox = params.get("sandbox") === "1";
  const { user, login, loading: authLoading } = useAuth();
  const [sandboxOutcome, setSandboxOutcome] = useState("approved");

  useEffect(() => {
    if (sandbox && !authLoading && !user?.is_staff) navigate("/admin/login", { replace: true });
  }, [sandbox, authLoading, user, navigate]);

  const [step, setStep] = useState(1);
  const [image, setImage] = useState(null);
  const [rawDataUrl, setRawDataUrl] = useState(null);
  const [frameId, setFrameId] = useState("royal");
  const [framedData, setFramedData] = useState(null);
  const [transform, setTransform] = useState({ scale: 1, rotate: 0, offsetX: 0, offsetY: 0, flipH: false });
  const [name, setName] = useState("");
  const [showName, setShowName] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);
  const [aiCaptions, setAiCaptions] = useState(null);
  const [genLoading, setGenLoading] = useState(false);
  const [hiddenFrames, setHiddenFrames] = useState(null);

  useEffect(() => {
    api.get("/toolkits/bgjcpt/settings").then(({ data }) => {
      const hidden = data.hidden_frames || [];
      setHiddenFrames(hidden);
      setFrameId((cur) => (hidden.includes(cur) ? (FRAMES.find((f) => !hidden.includes(f.id))?.id || cur) : cur));
    }).catch(() => setHiddenFrames([]));
  }, []); // runs once on mount; `api`/`FRAMES` are module constants

  const generateCaptions = async () => {
    setGenLoading(true);
    try {
      const { data } = await api.post("/captions/generate", {
        name,
        project_title: form.project_title,
        project_description: form.project_description,
        project_serves: form.project_serves,
        project_when: form.project_when,
        city: form.city,
        state: form.state,
      });
      if (data?.captions?.length) {
        setAiCaptions(data.captions);
        toast.success("Your captions are ready — written just for you! ✨");
      } else {
        toast.error("Couldn't write your captions. Please try again.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't write captions right now. Please try again in a moment.");
    } finally {
      setGenLoading(false);
    }
  };

  useEffect(() => {
    if (!editMode) return;
    (async () => {
      try {
        const { data } = await api.get("/me/winner");
        setFrameId(data.frame_id || "royal");
        setName(data.name || "");
        setShowName(data.show_name ?? true);
        setForm({
          email: data.email || "",
          age: data.age || "",
          project_title: data.project_title || "",
          project_description: data.project_description || "",
          project_serves: data.project_serves || "",
          project_when: data.project_when || "",
          city: data.city || "",
          state: data.state || "",
          quote: data.quote || "",
          instagram: data.instagram || "",
          media_contact_name: data.media_contact_name || "",
          media_contact_email: data.media_contact_email || "",
          media_contact_phone: data.media_contact_phone || "",
          gallery_opt_in: !!data.gallery_opt_in,
        });
        setResult({ slug: data.slug, landing_url: data.landing_url, share_url: data.share_url, framed_url: data.framed_url });
        toast.info("Editing your page. Re-upload your photo to change the frame image.");
      } catch {
        toast.error("Sign in to edit your page.");
        navigate("/login");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally re-runs only when edit mode toggles
  }, [editMode]);

  const onPhoto = (dataUrl, img) => {
    setRawDataUrl(dataUrl);
    setImage(img);
  };

  const recordFrame = async () => {
    if (sandbox) { toast.info("Sandbox: frame download not recorded."); return; }
    try {
      const framed = canvasRef.current ? canvasRef.current.toDataURL("image/jpeg", 0.9) : null;
      const raw = rawDataUrl ? await shrinkImage(rawDataUrl, 1600, 0.85) : null;
      await api.post("/frames", {
        toolkit: "bgjcpt",
        frame_id: frameId,
        name,
        project_title: form.project_title,
        project_description: form.project_description,
        project_serves: form.project_serves,
        project_when: form.project_when,
        city: form.city,
        state: form.state,
        framed_photo_base64: framed,
        raw_photo_base64: raw,
      });
    } catch (e) {
      console.warn("Frame download tracking failed", e);
    }
  };

  const goStep2 = () => {
    if (!image && !editMode) {
      toast.error("Add your photo first.");
      return;
    }
    if (image && canvasRef.current) {
      setFramedData(canvasRef.current.toDataURL("image/jpeg", 0.9));
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    if (!form.email || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) {
      toast.error("Please enter a valid email so we can send your toolkit.");
      return;
    }
    setSubmitting(true);
    if (sandbox) {
      const fakeSlug = (name || "your-name").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "your-name";
      const base = window.location.origin;
      setResult({ status: sandboxOutcome, slug: fakeSlug, email: form.email, framed_url: image ? framedData : null,
        share_url: `${base}/bgjcpt/${fakeSlug}`, landing_url: `${base}/bgjcpt/${fakeSlug}`, sandbox: true });
      setSubmitting(false);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (sandboxOutcome === "approved") fireConfetti();
      return;
    }
    try {
      const framed = image ? framedData : null;
      const raw = await shrinkImage(rawDataUrl, 1600, 0.85);
      const payload = {
        ...form,
        name,
        toolkit: "bgjcpt",
        show_name: showName,
        frame_id: frameId,
        framed_photo_base64: framed,
        raw_photo_base64: raw,
      };
      let data;
      if (editMode && result?.slug) {
        const resp = await api.put(`/winners/${result.slug}`, payload);
        data = resp.data;
      } else {
        const resp = await api.post("/winners", payload);
        data = resp.data;
      }
      setResult(data);
      setStep(3);
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (data.status === "approved") fireConfetti();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const emailAssets = async () => {
    if (sandbox) { toast.info("Sandbox: no email is sent."); return; }
    setSending(true);
    try {
      await api.post(`/winners/${result.slug}/send-assets`);
      toast.success(`Sent to ${result.email || form.email}! Check your inbox.`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send email right now. Please try again shortly.");
    } finally {
      setSending(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Link copied!");
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {sandbox && (
        <div data-testid="sandbox-banner" className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-pt-purple px-4 py-2 text-center text-xs font-bold text-white shadow-md sm:text-sm">
          <FlaskConical className="h-4 w-4" />
          <span>Sandbox preview — you're seeing exactly what a new winner sees. Nothing here is saved, tracked or emailed.</span>
          <Link to="/admin" data-testid="sandbox-exit-btn" className="inline-flex items-center gap-1 rounded-full bg-white/15 px-3 py-1 text-xs font-bold text-white hover:bg-white/25">
            <X className="h-3 w-3" /> Exit sandbox
          </Link>
        </div>
      )}
      <SiteNav />
      <div className="mx-auto w-full max-w-6xl px-5 py-10">
        {/* header */}
        <div className="mb-8">
          <Link to="/bgjcpt/welcome" data-testid="back-to-welcome-link" className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-sbg-raspberry hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to welcome
          </Link>
          <h1 className="font-display text-3xl font-black tracking-tight text-sbg-burgundy sm:text-4xl">
            #BlackGirlJoyChallenge Winner Toolkit
          </h1>
          <p className="mt-1 text-sm font-semibold text-pt-purple">
            In partnership with Megan Thee Stallion's Pete &amp; Thomas Foundation
          </p>
          <p className="mt-2 text-sbg-burgundy/70">Congratulations, Joy Maker! Let's share your win with the world. 🎉</p>
        </div>

        {/* stepper */}
        {step < 3 && (
          <div className="mb-8 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
            {[1, 2].map((n) => (
              <div key={n} className="flex items-center gap-3">
                <span
                  className={`flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold ${
                    step >= n ? "bg-sbg-fuchsia text-white" : "bg-sbg-blush text-sbg-raspberry"
                  }`}
                >
                  {n}
                </span>
                <span className={`text-sm font-bold ${step >= n ? "text-sbg-burgundy" : "text-sbg-burgundy/40"}`}>
                  {n === 1 ? "Your Frame" : "Your Page"}
                </span>
                {n === 1 && <span className="h-0.5 w-8 rounded bg-sbg-raspberry/20" />}
              </div>
            ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="animate-fade-up">
            <FrameEditor
              canvasRef={canvasRef}
              image={image}
              rawDataUrl={rawDataUrl}
              onPhoto={onPhoto}
              frameId={frameId}
              setFrameId={setFrameId}
              transform={transform}
              setTransform={setTransform}
              name={name}
              setName={setName}
              showName={showName}
              setShowName={setShowName}
              onDownload={recordFrame}
              hiddenFrames={hiddenFrames}
            />
            <CaptionCustomizer form={form} setForm={setForm} onGenerate={generateCaptions} generating={genLoading} />
            <ShareCaptions name={name} project={form.project_title} city={form.city} state={form.state} serves={form.project_serves} when={form.project_when} does={form.project_description} aiCaptions={aiCaptions} loading={genLoading} onReset={() => setAiCaptions(null)} />
            <div className="mt-8 flex justify-end">
              <button
                data-testid="continue-to-page-btn"
                onClick={goStep2}
                className="group inline-flex items-center gap-2 rounded-full bg-sbg-burgundy px-7 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-1"
              >
                Continue to your page
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
            <div className="rounded-3xl bg-white p-6 ring-1 ring-sbg-raspberry/10 sm:p-8">
              <h2 className="text-xl font-bold text-sbg-burgundy">Tell us about your joy project</h2>
              <p className="mb-6 mt-1 text-sm text-sbg-burgundy/70">
                Everything except your email is optional. Whatever you add shows on your page and press release.
              </p>
              <ProjectForm form={form} setForm={setForm} />
              {sandbox && (
                <div data-testid="sandbox-outcome" className="mt-6 rounded-2xl bg-pt-purple/10 p-4 ring-1 ring-pt-purple/20">
                  <div className="text-xs font-bold uppercase tracking-wider text-pt-purple">Sandbox: which outcome do you want to preview?</div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {[["approved", "Approved-list email → page goes live"], ["unverified", "New email → verify code first"], ["pending", "Not on list → pending review"]].map(([k, label]) => (
                      <button key={k} type="button" data-testid={`sandbox-outcome-${k}`} onClick={() => setSandboxOutcome(k)}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${sandboxOutcome === k ? "bg-pt-purple text-white" : "bg-white text-pt-purple ring-1 ring-pt-purple/30"}`}>{label}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-8 flex flex-wrap justify-between gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-sbg-blush px-5 py-3 text-sm font-bold text-sbg-raspberry"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to frame
                </button>
                <button
                  data-testid="create-page-btn"
                  onClick={submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-sbg-fuchsia px-7 py-3 text-base font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <PartyPopper className="h-5 w-5" />}
                  {editMode ? "Save my page" : "Create my page"}
                </button>
              </div>
            </div>
            <aside className="h-fit rounded-3xl bg-sbg-burgundy p-6 text-sbg-blush">
              <h3 className="font-display text-lg font-bold text-white">What happens next?</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li>✨ Your shareable winner page is created automatically.</li>
                <li>🔗 You get a link that shows your photo &amp; name when texted or posted.</li>
                <li>📰 A personal press release is ready for your local news.</li>
                <li>📧 We email everything to you.</li>
              </ul>
            </aside>
          </div>
        )}

        {step === 3 && result && result.status === "unverified" && (
          <VerifyEmailStep
            email={result.email || form.email}
            sandbox={sandbox}
            onVerified={(data) => {
              if (sandbox) { setResult((r) => ({ ...r, status: "approved" })); fireConfetti(); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
              if (data.token) login(data.token, { email: data.email, role: null, is_admin: false, is_staff: false, toolkit_scope: [], winner_slug: data.slug, winner_name: data.name });
              setResult(data);
              if (data.status === "approved") fireConfetti();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          />
        )}

        {step === 3 && result && result.status === "pending" && (
          <div className="animate-pop-in mx-auto max-w-2xl text-center" data-testid="pending-screen">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-pt-purple text-white">
              <ShieldCheck className="h-8 w-8" />
            </span>
            <h2 className="mt-5 font-display text-3xl font-black text-sbg-burgundy">Your page is pending review ✨</h2>
            <p className="mt-3 text-sbg-burgundy/70">
              To protect our winners and our community, a Southern Black Girls team member reviews every new page before
              it goes live. We'll email <span className="font-bold text-sbg-burgundy">{result.email || form.email}</span> the
              moment it's approved — usually within a day. Your frame and details are saved.
            </p>
            {result.framed_url && (
              <img src={result.framed_url} alt="Your winner frame" className="mx-auto mt-6 w-56 rounded-2xl opacity-90 shadow-xl" />
            )}
            <div className="mt-7">
              <Link to="/" className="inline-flex items-center gap-2 rounded-full bg-sbg-burgundy px-6 py-3 font-bold text-white transition-transform hover:-translate-y-0.5">
                Back to toolkits
              </Link>
            </div>
            <p className="mt-6 text-xs text-sbg-burgundy/50">
              Sign in with your email anytime to keep editing while you wait.
            </p>
          </div>
        )}

        {step === 3 && result && result.status === "approved" && (
          <div className="animate-pop-in mx-auto max-w-2xl text-center">
            <span className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-sbg-fuchsia text-white">
              <PartyPopper className="h-8 w-8" />
            </span>
            <h2 className="mt-5 font-display text-3xl font-black text-sbg-burgundy">Your page is live! 🎉</h2>
            <p className="mt-2 text-sbg-burgundy/70">Share this link anywhere — your photo and name show in the preview.</p>

            {result.framed_url && (
              <img
                src={result.framed_url}
                alt="Your winner frame"
                className="mx-auto mt-6 w-64 rounded-2xl shadow-xl"
              />
            )}

            <div className="mt-6 flex items-center gap-2 rounded-full bg-white p-2 pl-5 ring-1 ring-sbg-raspberry/15">
              <span data-testid="share-url-text" className="flex-1 truncate text-left text-sm font-medium text-sbg-burgundy">
                {result.share_url}
              </span>
              <button
                data-testid="copy-share-btn"
                onClick={() => copy(result.share_url)}
                className="flex items-center gap-1.5 rounded-full bg-sbg-raspberry px-4 py-2 text-sm font-bold text-white"
              >
                <Copy className="h-4 w-4" /> Copy
              </button>
            </div>

            <div className="mt-7 grid gap-3 sm:grid-cols-3">
              <a
                href={result.landing_url}
                target="_blank"
                rel="noreferrer"
                data-testid="view-page-btn"
                onClick={(e) => { if (sandbox) { e.preventDefault(); toast.info("Sandbox: no real page was created."); } }}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-4 text-sm font-bold text-sbg-burgundy ring-1 ring-sbg-raspberry/15 transition-transform hover:-translate-y-0.5"
              >
                <ExternalLink className="h-4 w-4" /> View my page
              </a>
              <button
                data-testid="email-assets-btn"
                onClick={emailAssets}
                disabled={sending}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-sbg-turquoise px-4 py-4 text-sm font-bold text-sbg-burgundy transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email it to me
              </button>
            </div>
            <p className="mt-6 rounded-2xl bg-sbg-blush/40 px-4 py-3 text-xs text-sbg-burgundy/70" data-testid="pr-pending-note">
              <span className="font-bold text-sbg-burgundy">Press release:</span> our team is writing and reviewing a personal press release for you. We'll email you a copy once it's approved.
            </p>
            <p className="mt-6 text-xs text-sbg-burgundy/50">
              Want to change something later? Sign in with your email to edit your page anytime.
            </p>
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
